<template>
  <head>
    <meta charset="utf-8" />
    <title>CBHI | Claims Management Dashboard</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="Process and manage insurance claims" name="description" />
    <meta content="CBHI System" name="author" />
    <link rel="shortcut icon" href="assets/images/favicon.svg">
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body data-sidebar="dark">
  <div id="layout-wrapper">
    <NavigationBar/>

    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Claims Management Dashboard</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="javascript: void(0);">Claims Management</a></li>
                    <li class="breadcrumb-item active">Dashboard</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Ethiopian/Gregorian Date Filter Section -->
          <div class="row mb-3">
            <div class="col-12">
              <div class="card mb-0">
                <div class="card-body py-2">
                  <!-- Row 1: Calendar Toggle + Date Selection -->
                  <div class="row g-2 align-items-center">
                    <!-- Calendar Type Toggle -->
                    <div class="col-auto">
                      <div class="btn-group btn-group-sm" role="group">
                        <input type="radio" class="btn-check" name="calendarType" id="calEth" :value="true" v-model="showEthiopianDates">
                        <label class="btn btn-outline-primary" for="calEth">
                          <i class="bx bx-calendar me-1"></i>Ethiopian
                        </label>
                        <input type="radio" class="btn-check" name="calendarType" id="calGreg" :value="false" v-model="showEthiopianDates">
                        <label class="btn btn-outline-success" for="calGreg">
                          <i class="bx bx-calendar-alt me-1"></i>Gregorian
                        </label>
                      </div>
                    </div>

                    <!-- Date Selection -->
                    <div class="col">
                      <div class="row g-2 align-items-center">
                        <!-- From Date -->
                        <div class="col-auto">
                          <span class="badge bg-secondary">From</span>
                        </div>
                        <div class="col-auto">
                          <select v-model="dateFilterRange.fromDay" @change="updateDateRange" class="form-select form-select-sm" style="width: 65px;">
                            <option value="">{{ showEthiopianDates ? 'ቀን' : 'Day' }}</option>
                            <option v-for="d in fromDaysInMonth" :key="d" :value="d">{{ d }}</option>
                          </select>
                        </div>
                        <div class="col-auto">
                          <select v-model="dateFilterRange.fromMonth" @change="onFromMonthChange" class="form-select form-select-sm" style="width: 115px;">
                            <option value="">{{ showEthiopianDates ? 'ወር' : 'Month' }}</option>
                            <option v-for="m in displayMonths" :key="m.value" :value="m.value">
                              {{ showEthiopianDates ? m.label : m.labelShort }}
                            </option>
                          </select>
                        </div>
                        <div class="col-auto">
                          <select v-model="dateFilterRange.fromYear" @change="updateDateRange" class="form-select form-select-sm" style="width: 80px;">
                            <option value="">{{ showEthiopianDates ? 'ዓ/ም' : 'Year' }}</option>
                            <option v-for="y in displayYearOptions" :key="y" :value="y">{{ y }}</option>
                          </select>
                        </div>

                        <!-- Separator -->
                        <div class="col-auto">
                          <span class="text-muted">—</span>
                        </div>

                        <!-- To Date -->
                        <div class="col-auto">
                          <span class="badge bg-secondary">To</span>
                        </div>
                        <div class="col-auto">
                          <select v-model="dateFilterRange.toDay" @change="updateDateRange" class="form-select form-select-sm" style="width: 65px;">
                            <option value="">{{ showEthiopianDates ? 'ቀን' : 'Day' }}</option>
                            <option v-for="d in toDaysInMonth" :key="d" :value="d">{{ d }}</option>
                          </select>
                        </div>
                        <div class="col-auto">
                          <select v-model="dateFilterRange.toMonth" @change="onToMonthChange" class="form-select form-select-sm" style="width: 115px;">
                            <option value="">{{ showEthiopianDates ? 'ወር' : 'Month' }}</option>
                            <option v-for="m in displayMonths" :key="m.value" :value="m.value">
                              {{ showEthiopianDates ? m.label : m.labelShort }}
                            </option>
                          </select>
                        </div>
                        <div class="col-auto">
                          <select v-model="dateFilterRange.toYear" @change="updateDateRange" class="form-select form-select-sm" style="width: 80px;">
                            <option value="">{{ showEthiopianDates ? 'ዓ/ም' : 'Year' }}</option>
                            <option v-for="y in displayYearOptions" :key="y" :value="y">{{ y }}</option>
                          </select>
                        </div>

                        <!-- Action Buttons -->
                        <div class="col-auto">
                          <button type="button" class="btn btn-primary btn-sm" @click="applyDateFilter" :disabled="!isValidDateRange" title="Apply Filter">
                            <i class="bx bx-filter-alt"></i>
                          </button>
                        </div>
                        <div class="col-auto" v-if="hasDateSelection">
                          <button type="button" class="btn btn-outline-danger btn-sm" @click="clearDateFilter" title="Clear Filter">
                            <i class="bx bx-x"></i>
                          </button>
                        </div>

                        <!-- Quick Date Presets -->
                        <div class="col-auto ms-2">
                          <div class="btn-group btn-group-sm" role="group">
                            <button type="button" class="btn btn-outline-secondary" @click="setQuickDateRange('today')" title="Today">Today</button>
                            <button type="button" class="btn btn-outline-secondary" @click="setQuickDateRange('week')" title="This Week">Week</button>
                            <button type="button" class="btn btn-outline-secondary" @click="setQuickDateRange('month')" title="This Month">Month</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Row 2: Alternate Calendar Display -->
                  <div class="row mt-2" v-if="alternateDateDisplay">
                    <div class="col-12">
                      <small :class="showEthiopianDates ? 'text-success' : 'text-primary'">
                        <i class="bx bx-calendar-check me-1"></i>
                        <strong>{{ showEthiopianDates ? 'Gregorian' : 'Ethiopian' }}:</strong>
                        {{ alternateDateDisplay }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Primary Statistics Row -->
          <div class="row">
            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Total Claims</p>
                      <h5 class="mb-0">{{ statistics.summary_statistics?.total_count || 0 }}</h5>
                      <small class="text-muted">
                        From {{ formatDateDisplay(statistics.summary_statistics?.earliest_claim_date) }}
                        to {{ formatDateDisplay(statistics.summary_statistics?.latest_claim_date) }}
                      </small>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-info rounded-circle text-white font-size-20">
                        <i class="bx bx-file-blank"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Submitted Claims</p>
                      <h5 class="mb-0">{{ statistics.summary_statistics?.submitted_count || 0 }}</h5>
                      <small class="text-warning">
                        <i class="bx bx-time-five"></i>
                        {{ statistics.summary_statistics?.pending_rate || 0 }}% pending
                      </small>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-warning rounded-circle text-white font-size-20">
                        <i class="bx bx-time-five"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Approved Claims</p>
                      <h5 class="mb-0">{{ statistics.summary_statistics?.approved_count || 0 }}</h5>
                      <small class="text-success">
                        <i class="bx bx-check-circle"></i>
                        {{ statistics.summary_statistics?.approval_rate || 0 }}% approval rate
                      </small>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-success rounded-circle text-white font-size-20">
                        <i class="bx bx-check-circle"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Rejected Claims</p>
                      <h5 class="mb-0">{{ statistics.summary_statistics?.rejected_count || 0 }}</h5>
                      <small class="text-danger">
                        <i class="bx bx-x-circle"></i>
                        {{ statistics.summary_statistics?.rejection_rate || 0 }}% rejection rate
                      </small>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-danger rounded-circle text-white font-size-20">
                        <i class="bx bx-x-circle"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Financial Metrics Row -->
          <div class="row">
            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Pending Amount</p>
                      <h5 class="mb-0">ETB {{ formatAmount(statistics.summary_statistics?.pending_amount || 0) }}</h5>
                      <small class="text-muted">{{ statistics.summary_statistics?.submitted_count || 0 }} claims</small>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-primary rounded-circle text-white font-size-20">
                        <i class="bx bx-money"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Total Approved</p>
                      <h5 class="mb-0">ETB {{ formatAmount(statistics.summary_statistics?.total_approved_amount || 0) }}</h5>
                      <small class="text-success">{{ statistics.summary_statistics?.approved_count || 0 }} claims</small>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-success rounded-circle text-white font-size-20">
                        <i class="bx bx-wallet"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Patient Contribution</p>
                      <h5 class="mb-0">ETB {{ formatAmount(statistics.summary_statistics?.total_patient_contribution || 0) }}</h5>
                      <small class="text-info">
                        <i class="bx bx-user"></i>
                        {{ statistics.summary_statistics?.patient_contribution_rate || 0 }}% of approved
                      </small>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-info rounded-circle text-white font-size-20">
                        <i class="bx bx-user"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Average Claim</p>
                      <h5 class="mb-0">ETB {{ formatAmount(statistics.summary_statistics?.average_claim_amount || 0) }}</h5>
                      <small class="text-muted">Per claim</small>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-secondary rounded-circle text-white font-size-20">
                        <i class="bx bx-calculator"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Performance Metrics Row -->
          <div class="row">
            <div class="col-lg-4 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Avg Processing Days</p>
                      <h5 class="mb-0">{{ formatDecimal(statistics.summary_statistics?.avg_processing_days || 0) }} days</h5>
                      <small :class="getProcessingTimeClass(statistics.summary_statistics?.avg_processing_days)">
                        <i :class="getProcessingTimeIcon(statistics.summary_statistics?.avg_processing_days)"></i>
                        {{ getProcessingTimeLabel(statistics.summary_statistics?.avg_processing_days) }}
                      </small>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-info rounded-circle text-white font-size-20">
                        <i class="bx bx-trending-up"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-4 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Overdue Claims</p>
                      <h5 class="mb-0">{{ statistics.summary_statistics?.overdue_count || 0 }}</h5>
                      <small class="text-danger">
                        <i class="bx bx-error-circle"></i>
                        Require attention
                      </small>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-danger rounded-circle text-white font-size-20">
                        <i class="bx bx-error-circle"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-lg-4 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex flex-wrap">
                    <div class="me-3">
                      <p class="text-muted mb-2">Paid Claims</p>
                      <h5 class="mb-0">{{ statistics.summary_statistics?.paid_count || 0 }}</h5>
                      <small class="text-success">
                        <i class="bx bx-check-double"></i>
                        Completed
                      </small>
                    </div>
                    <div class="avatar-sm ms-auto">
                      <div class="avatar-title bg-success rounded-circle text-white font-size-20">
                        <i class="bx bx-check-double"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Analytics Cards Row -->
          <div class="row">
            <!-- Monthly Trends -->
            <div class="col-lg-6">
              <div class="card">
                <div class="card-header bg-white border-bottom">
                  <h5 class="card-title mb-0">
                    <i class="bx bx-trending-up text-primary me-2"></i>
                    Monthly Trends
                  </h5>
                </div>
                <div class="card-body">
                  <div v-if="statistics.monthly_trends && statistics.monthly_trends.length > 0">
                    <div v-for="trend in statistics.monthly_trends" :key="trend.month_year" class="mb-3">
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <h6 class="mb-0">{{ formatMonthYearDisplay(trend.month_year) }}</h6>
                        <span class="badge bg-primary">{{ trend.total_claims }} claims</span>
                      </div>
                      <div class="row">
                        <div class="col-6">
                          <small class="text-muted">Claimed Amount</small>
                          <div class="fw-bold text-info">ETB {{ formatAmount(trend.total_claimed_amount) }}</div>
                        </div>
                        <div class="col-6">
                          <small class="text-muted">Approved Amount</small>
                          <div class="fw-bold text-success">ETB {{ formatAmount(trend.approved_amount) }}</div>
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-4">
                          <small class="text-success">Approved: {{ trend.approved_claims }}</small>
                        </div>
                        <div class="col-4">
                          <small class="text-danger">Rejected: {{ trend.rejected_claims }}</small>
                        </div>
                        <div class="col-4">
                          <small class="text-warning">Pending: {{ trend.submitted_claims }}</small>
                        </div>
                      </div>
                      <hr v-if="statistics.monthly_trends.length > 1" class="my-3">
                    </div>
                  </div>
                  <div v-else class="text-center text-muted py-3">
                    <i class="bx bx-calendar font-size-24"></i>
                    <p class="mt-2">No monthly data available</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Top Hospitals -->
            <div class="col-lg-6">
              <div class="card">
                <div class="card-header bg-white border-bottom">
                  <h5 class="card-title mb-0">
                    <i class="bx bx-hospital text-success me-2"></i>
                    Top Hospitals
                  </h5>
                </div>
                <div class="card-body">
                  <div v-if="statistics.top_hospitals && statistics.top_hospitals.length > 0">
                    <div v-for="(hospital, index) in statistics.top_hospitals" :key="hospital.license_number" class="mb-3">
                      <div class="d-flex align-items-center mb-2">
                        <div class="avatar-sm me-3">
                          <div class="avatar-title bg-light text-primary rounded-circle font-weight-bold">
                            {{ index + 1 }}
                          </div>
                        </div>
                        <div class="flex-grow-1">
                          <h6 class="mb-0">{{ hospital.hospital_name }}</h6>
                          <small class="text-muted">{{ hospital.license_number }}</small>
                        </div>
                        <div class="text-end">
                          <span class="badge bg-primary">{{ hospital.total_claims }} claims</span>
                        </div>
                      </div>
                      <div class="row">
                        <div class="col-6">
                          <small class="text-muted">Approved Amount</small>
                          <div class="fw-bold text-success">ETB {{ formatAmount(hospital.approved_amount) }}</div>
                        </div>
                        <div class="col-6">
                          <small class="text-muted">Success Rate</small>
                          <div class="fw-bold text-primary">{{ formatDecimal(hospital.approval_rate) }}%</div>
                        </div>
                      </div>
                      <div class="row mt-2">
                        <div class="col-6">
                          <small class="text-success">Approved: {{ hospital.approved_claims }}</small>
                        </div>
                        <div class="col-6">
                          <small class="text-danger">Rejected: {{ hospital.rejected_claims }}</small>
                        </div>
                      </div>
                      <div class="progress mt-2" style="height: 5px;">
                        <div class="progress-bar bg-success" role="progressbar" :style="{width: hospital.approval_rate + '%'}"></div>
                      </div>
                      <hr v-if="index < statistics.top_hospitals.length - 1" class="my-3">
                    </div>
                  </div>
                  <div v-else class="text-center text-muted py-3">
                    <i class="bx bx-hospital font-size-24"></i>
                    <p class="mt-2">No hospital data available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Rejection Analysis -->
          <div class="row" v-if="statistics.rejection_analysis && statistics.rejection_analysis.length > 0">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white border-bottom">
                  <h5 class="card-title mb-0">
                    <i class="bx bx-pie-chart-alt-2 text-danger me-2"></i>
                    Rejection Analysis
                  </h5>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div v-for="rejection in statistics.rejection_analysis" :key="rejection.rejection_category" class="col-lg-3 col-md-6 mb-3">
                      <div class="card border border-danger">
                        <div class="card-body text-center">
                          <div class="avatar-md mx-auto mb-3">
                            <div class="avatar-title bg-danger-subtle text-danger rounded-circle">
                              <i class="bx bx-x-circle font-size-24"></i>
                            </div>
                          </div>
                          <h5 class="text-danger">{{ rejection.rejection_count }}</h5>
                          <p class="text-muted mb-2">{{ formatRejectionCategory(rejection.rejection_category) }}</p>
                          <span class="badge bg-danger">{{ formatDecimal(rejection.percentage) }}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Report Metadata -->
          <div class="row">
            <div class="col-12">
              <div class="card bg-light">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-md-8">
                      <h6 class="mb-1">
                        <i class="bx bx-info-circle text-info me-2"></i>
                        Report Information
                      </h6>
                      <small class="text-muted">
                        Report generated at: {{ formatDateTimeDisplay(statistics.metadata?.report_generated_at) }} |
                        Access scope: {{ formatAccessScope(statistics.metadata?.user_access_scope) }}
                        <span v-if="hasActiveFilters"> | Filters applied</span>
                      </small>
                    </div>
                    <div class="col-md-4 text-md-end">
                      <button @click="loadStatistics" class="btn btn-sm btn-outline-primary">
                        <i class="bx bx-refresh me-1"></i>
                        Refresh Statistics
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Claims Processing Table -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white d-flex justify-content-between align-items-center w-100 border-bottom-1 pb-3 border-bottom">
                  <!-- Filters -->
                  <div class="me-3 text-nowrap filter-tool-bar-btns">
                    <!-- Status Filter -->
                    <div class="btn-group">
                      <button type="button" class="btn btn-light waves-effect" data-bs-toggle="dropdown">
                        <i class="bx bx-filter font-size-16 me-1 align-middle"></i>
                        {{ filters.status ? getStatusLabel(filters.status) : 'All Status' }}
                      </button>
                      <div class="dropdown-menu">
                        <a class="dropdown-item" @click="filterByStatus('')">All Status</a>
                        <a class="dropdown-item" @click="filterByStatus('submitted')">
                          <span class="badge bg-warning me-2"></span>Submitted
                        </a>
                        <a class="dropdown-item" @click="filterByStatus('approved')">
                          <span class="badge bg-success me-2"></span>Approved
                        </a>
                        <a class="dropdown-item" @click="filterByStatus('rejected')">
                          <span class="badge bg-danger me-2"></span>Rejected
                        </a>
                      </div>
                    </div>

                    <!-- Priority Filter -->
                    <div class="btn-group ms-2">
                      <button type="button" class="btn btn-light waves-effect" data-bs-toggle="dropdown">
                        <i class="bx bx-flag font-size-16 me-1 align-middle"></i>
                        {{ filters.priority ? getPriorityLabel(filters.priority) : 'All Priority' }}
                      </button>
                      <div class="dropdown-menu">
                        <a class="dropdown-item" @click="filterByPriority('')">All Priority</a>
                        <a class="dropdown-item" @click="filterByPriority('urgent')">
                          <span class="badge bg-danger me-2">Urgent</span> >7 days
                        </a>
                        <a class="dropdown-item" @click="filterByPriority('high')">
                          <span class="badge bg-warning me-2">High</span> 4-7 days
                        </a>
                        <a class="dropdown-item" @click="filterByPriority('normal')">
                          <span class="badge bg-primary me-2">Normal</span> {{'< 4 days'}}
                        </a>
                      </div>
                    </div>

                    <!-- Clear Filters -->
                    <button v-if="hasActiveFilters" type="button" class="btn btn-danger waves-effect ms-2" @click="clearFilters">
                      <i class="bx bx-x font-size-16 me-1 align-middle"></i> Clear
                    </button>
                  </div>

                  <div class="btn-toolbar d-flex justify-content-between align-items-center w-100" role="toolbar">
                    <div class="d-flex align-items-center gap-3">
                      <!-- Per Page Selector -->
                      <div class="d-flex align-items-center">
                        <label class="me-2 text-nowrap small">Show</label>
                        <select class="form-select form-select-sm" style="width: 80px;" v-model="perPage" @change="onPerPageChange">
                          <option :value="10">10</option>
                          <option :value="25">25</option>
                          <option :value="50">50</option>
                          <option :value="100">100</option>
                        </select>
                        <label class="ms-2 text-nowrap small">entries</label>
                      </div>

                      <!-- Search Box -->
                      <div class="search-box mb-0">
                        <div class="position-relative">
                          <input v-model="searchQuery" @input="handleSearch" type="text"
                                 class="form-control bg-light border-light rounded"
                                 placeholder="Search by claim number, member name, CBHI number...">
                          <i class="bx bx-search-alt search-icon"></i>
                        </div>
                      </div>

                      <!-- Refresh Button -->
                      <button type="button" class="btn btn-sm btn-outline-secondary" @click="refreshData" :disabled="loading">
                        <i class="bx bx-refresh" :class="{'bx-spin': loading}"></i>
                      </button>
                    </div>

                    <div class="d-flex">
                      <!-- Batch Actions -->
                      <div v-if="selectedClaims.length > 0" class="me-2">
                        <div class="btn-group">
                          <button type="button" class="btn btn-success dropdown-toggle" data-bs-toggle="dropdown">
                            <i class="bx bx-check-square me-1"></i> Batch Actions ({{ selectedClaims.length }})
                          </button>
                          <div class="dropdown-menu">
                            <a class="dropdown-item" @click="batchApprove">
                              <i class="bx bx-check-circle text-success me-2"></i> Approve Selected
                            </a>
                            <a class="dropdown-item" @click="batchReject">
                              <i class="bx bx-x-circle text-danger me-2"></i> Reject Selected
                            </a>
                            <div class="dropdown-divider"></div>
                            <a class="dropdown-item" @click="clearSelection">
                              <i class="bx bx-x me-2"></i> Clear Selection
                            </a>
                          </div>
                        </div>
                      </div>

                      <!-- Settlement -->
                      <button @click="showSettlementModal = true" type="button" class="btn btn-primary">
                        <i class="bx bx-wallet"></i> <span class="d-md-inline ms-1">Create Settlement</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div class="card-body">
                  <!-- Loading State -->
                  <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="mt-3">Loading claims...</p>
                  </div>

                  <!-- No Data State -->
                  <div v-else-if="!loading && claims.length === 0" class="text-center py-5">
                    <i class="bx bx-file-blank text-muted" style="font-size: 48px;"></i>
                    <p class="mt-3 text-muted">No claims found</p>
                    <button v-if="hasActiveFilters" @click="clearFilters" class="btn btn-outline-primary">
                      Clear Filters
                    </button>
                  </div>

                  <!-- Data Table -->
                  <div v-else class="table-responsive">
                    <table class="table align-middle table-hover border table-striped text-nowrap mb-0">
                      <thead class="table-light">
                      <tr>
                        <th scope="col">
                          <div class="form-check">
                            <input class="form-check-input" type="checkbox" @change="toggleSelectAll"
                                   :checked="isAllSelected" :disabled="claims.filter(c => c.status === 'submitted').length === 0">
                          </div>
                        </th>
                        <th scope="col">#</th>
                        <th scope="col">Claim Details</th>
                        <th scope="col">Member</th>
                        <th scope="col">Hospital</th>
                        <th scope="col">Service Date</th>
                        <th scope="col">Days Pending</th>
                        <th scope="col">Status</th>
                        <th scope="col" class="text-end">Amount</th>
                        <th scope="col" class="text-end">Coverage Balance</th>
                        <th scope="col" class="text-center">Actions</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(claim, index) in claims" :key="claim.claim_id"
                          :class="{'table-danger': claim.days_pending > 7 && claim.status === 'submitted'}">
                        <td>
                          <div class="form-check" v-if="claim.status === 'submitted'">
                            <input class="form-check-input" type="checkbox" v-model="selectedClaims" :value="claim.claim_id">
                          </div>
                        </td>
                        <td>{{ getRowNumber(index) }}</td>
                        <td>
                          <div>
                            <h6 class="mb-0">{{ claim.claim_number }}</h6>
                            <small class="text-muted">{{ formatDateDisplay(claim.claim_date) }}</small>
                            <div v-if="claim.diagnosis" class="mt-1">
                              <small class="text-info">{{ truncateText(claim.diagnosis, 50) }}</small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div class="d-flex align-items-center">
                            <div class="avatar-xs me-2">
                              <div class="avatar-title bg-primary text-white rounded-circle">
                                {{ getInitials(claim.full_name) }}
                              </div>
                            </div>
                            <div>
                              <h6 class="mb-0">{{ claim.full_name }}</h6>
                              <small class="text-muted">{{ claim.cbhi_number }}</small>
                              <div class="mt-1">
                                <span v-if="!claim.member_active" class="badge bg-danger">Inactive</span>
                                <span v-if="!claim.coverage_eligible" class="badge bg-warning">Not Eligible</span>
                                <span v-if="claim.coverage_type === 'unlimited'" class="badge bg-success">Unlimited</span>
                                <span v-else-if="claim.coverage_type === 'limited'" class="badge bg-info">Limited</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <h6 class="mb-0">{{ claim.hospital_name }}</h6>
                            <small class="text-muted">{{ claim.woreda_name }}</small>
                            <div class="mt-1">
                              <small class="text-muted">{{ claim.license_number }}</small>
                            </div>
                          </div>
                        </td>
                        <td>{{ formatDateDisplay(claim.service_date) }}</td>
                        <td>
                          <span :class="getPriorityBadge(claim.days_pending)">
                            {{ claim.days_pending }} days
                          </span>
                        </td>
                        <td>
                          <span :class="getStatusBadge(claim.status)">
                            {{ getStatusLabel(claim.status) }}
                          </span>
                          <div v-if="claim.status === 'approved' && claim.approval_reason" class="mt-1">
                            <small class="text-success">{{ getApprovalReasonLabel(claim.approval_reason) }}</small>
                          </div>
                          <div v-if="claim.status === 'rejected' && claim.rejection_reason" class="mt-1">
                            <small class="text-danger">{{ getRejectionReasonLabel(claim.rejection_reason) }}</small>
                          </div>
                        </td>
                        <td class="text-end">
                          <div>
                            <div class="fw-bold">ETB {{ formatAmount(claim.total_amount) }}</div>
                            <div v-if="claim.approved_amount && claim.status === 'approved'" class="text-success">
                              Approved: ETB {{ formatAmount(claim.approved_amount) }}
                            </div>
                            <div v-if="claim.patient_contribution > 0" class="text-warning">
                              Patient: ETB {{ formatAmount(claim.patient_contribution) }}
                            </div>
                          </div>
                        </td>
                        <td class="text-end">
                          <div v-if="claim.coverage_type === 'unlimited' || !claim.annual_limit || claim.annual_limit === 0">
                            <div class="text-center">
                              <span class="badge bg-success-subtle text-success px-3 py-2 mb-1">
                                <i class="bx bx-infinite font-size-16"></i> UNLIMITED
                              </span>
                              <div class="text-muted small">No Coverage Limits</div>
                            </div>
                          </div>
                          <div v-else>
                            <div>
                              <div class="text-success fw-bold">ETB {{ formatAmount(claim.remaining_balance) }}</div>
                              <div class="text-muted small">of ETB {{ formatAmount(claim.annual_limit) }}</div>
                              <div class="progress mt-2" style="height: 5px;">
                                <div class="progress-bar" role="progressbar"
                                     :style="{width: getUtilizationPercentage(claim) + '%'}"
                                     :class="getUtilizationClass(claim)">
                                </div>
                              </div>
                              <small class="text-muted">
                                {{ getUtilizationPercentage(claim).toFixed(1) }}% used
                              </small>
                            </div>
                          </div>
                        </td>
                        <td class="text-center">
                          <button @click="viewClaimDetails(claim)" class="btn btn-sm btn-outline-info" title="View Details">
                            <i class="bx bx-show"></i>
                          </button>
                          <button v-if="claim.status === 'submitted'" @click="processClaim(claim)"
                                  class="btn btn-sm btn-success ms-1" title="Process Claim">
                            <i class="bx bx-check-circle"></i>
                          </button>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Pagination -->
                  <div class="d-flex justify-content-between align-items-center mt-3" v-if="pagination.total > 0">
                    <div class="text-muted small">
                      Showing {{ showingFrom }} to {{ showingTo }} of {{ pagination.total }} entries
                    </div>
                    <nav aria-label="Page navigation">
                      <ul class="pagination pagination-sm mb-0">
                        <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                          <a class="page-link" href="#" @click.prevent="goToPage(1)" title="First">
                            <i class="bx bx-chevrons-left"></i>
                          </a>
                        </li>
                        <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                          <a class="page-link" href="#" @click.prevent="previousPage" title="Previous">
                            <i class="bx bx-chevron-left"></i>
                          </a>
                        </li>
                        <li class="page-item" v-for="page in visiblePages" :key="page"
                            :class="{ active: page === pagination.current_page }">
                          <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
                        </li>
                        <li class="page-item" :class="{ disabled: pagination.current_page === pagination.total_pages }">
                          <a class="page-link" href="#" @click.prevent="nextPage" title="Next">
                            <i class="bx bx-chevron-right"></i>
                          </a>
                        </li>
                        <li class="page-item" :class="{ disabled: pagination.current_page === pagination.total_pages }">
                          <a class="page-link" href="#" @click.prevent="goToPage(pagination.total_pages)" title="Last">
                            <i class="bx bx-chevrons-right"></i>
                          </a>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modals -->
      <ClaimDetailsModal
          v-if="showDetailsModal"
          :claim="selectedClaim"
          @close="closeDetailsModal"
          @process="processClaim"
          @approve="approveClaim"
          @reject="rejectClaim"
      />

      <ClaimProcessModal
          v-if="showProcessModal"
          :claim="selectedClaim"
          @close="closeProcessModal"
          @approve="approveClaim"
          @reject="rejectClaim"
      />

      <BatchProcessModal
          v-if="showBatchModal"
          :claims="selectedClaimsData"
          :action="batchAction"
          @close="closeBatchModal"
          @process="processBatch"
      />

      <SettlementModal
          v-if="showSettlementModal"
          @close="showSettlementModal = false"
          @created="onSettlementCreated"
      />

      <!-- Alerts -->
      <div v-if="alert.success" class="position-fixed top-0 end-0 p-3" style="z-index: 9999;">
        <div class="alert alert-success alert-dismissible fade show" role="alert">
          <i class="bx bx-check-circle me-2"></i>
          {{ message }}
          <button type="button" class="btn-close" @click="clearAlerts"></button>
        </div>
      </div>

      <div v-if="alert.failed" class="position-fixed top-0 end-0 p-3" style="z-index: 9999;">
        <div class="alert alert-danger alert-dismissible fade show" role="alert">
          <i class="bx bx-error-circle me-2"></i>
          {{ message }}
          <button type="button" class="btn-close" @click="clearAlerts"></button>
        </div>
      </div>

      <div v-if="alert.info" class="position-fixed top-0 end-0 p-3" style="z-index: 9999;">
        <div class="alert alert-info alert-dismissible fade show" role="alert">
          <i class="bx bx-info-circle me-2"></i>
          {{ message }}
          <button type="button" class="btn-close" @click="clearAlerts"></button>
        </div>
      </div>

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">© CBHI</div>
            <div class="col-sm-6">
              <div class="text-sm-end d-sm-block">
                Ministry of Health - Ethiopia
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </div>
  <div class="rightbar-overlay"></div>
  </body>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ClaimDetailsModal from "@/components/cbhi-claims-management/modals/ClaimDetailsModal";
import ClaimProcessModal from "@/components/cbhi-claims-management/modals/ClaimProcessModal.vue";
import BatchProcessModal from "@/components/cbhi-claims-management/modals/BatchProcessModal.vue";
import SettlementModal from "@/components/cbhi-claims-management/modals/SettlementModal.vue";
import { authUrl, executeJson } from "@/api";
import router from "@/router";

export default {
  name: "ClaimsManagementDashboard",
  components: {
    NavigationBar,
    ClaimDetailsModal,
    ClaimProcessModal,
    BatchProcessModal,
    SettlementModal
  },
  data() {
    return {
      // Loading states
      loading: false,

      // Ethiopian Calendar State
      showEthiopianDates: true,
      currentEthiopianDate: { year: 2017, month: 3, day: 15, monthName: 'ህዳር', monthNameEn: 'Hidar' },

      // Date Filter Range (works for both Ethiopian and Gregorian)
      dateFilterRange: {
        fromDay: '',
        fromMonth: '',
        fromYear: '',
        toDay: '',
        toMonth: '',
        toYear: ''
      },

      // Ethiopian months list
      ethiopianMonths: [
        { value: 1, label: 'መስከረም', labelEn: 'Meskerem' },
        { value: 2, label: 'ጥቅምት', labelEn: 'Tikimt' },
        { value: 3, label: 'ህዳር', labelEn: 'Hidar' },
        { value: 4, label: 'ታህሳስ', labelEn: 'Tahsas' },
        { value: 5, label: 'ጥር', labelEn: 'Tir' },
        { value: 6, label: 'የካቲት', labelEn: 'Yekatit' },
        { value: 7, label: 'መጋቢት', labelEn: 'Megabit' },
        { value: 8, label: 'ሚያዝያ', labelEn: 'Miyazya' },
        { value: 9, label: 'ግንቦት', labelEn: 'Ginbot' },
        { value: 10, label: 'ሰኔ', labelEn: 'Sene' },
        { value: 11, label: 'ሐምሌ', labelEn: 'Hamle' },
        { value: 12, label: 'ነሐሴ', labelEn: 'Nehase' },
        { value: 13, label: 'ጳጉሜ', labelEn: 'Pagume' }
      ],

      // Gregorian months list
      gregorianMonths: [
        { value: 1, label: 'January', labelShort: 'Jan' },
        { value: 2, label: 'February', labelShort: 'Feb' },
        { value: 3, label: 'March', labelShort: 'Mar' },
        { value: 4, label: 'April', labelShort: 'Apr' },
        { value: 5, label: 'May', labelShort: 'May' },
        { value: 6, label: 'June', labelShort: 'Jun' },
        { value: 7, label: 'July', labelShort: 'Jul' },
        { value: 8, label: 'August', labelShort: 'Aug' },
        { value: 9, label: 'September', labelShort: 'Sep' },
        { value: 10, label: 'October', labelShort: 'Oct' },
        { value: 11, label: 'November', labelShort: 'Nov' },
        { value: 12, label: 'December', labelShort: 'Dec' }
      ],

      // Data
      claims: [],
      selectedClaim: null,
      selectedClaims: [],
      selectedClaimsData: [],

      // Statistics
      statistics: {
        summary_statistics: {
          total_count: 0,
          submitted_count: 0,
          approved_count: 0,
          rejected_count: 0,
          paid_count: 0,
          overdue_count: 0,
          pending_amount: 0,
          total_approved_amount: 0,
          total_patient_contribution: 0,
          avg_processing_days: 0,
          earliest_claim_date: null,
          latest_claim_date: null,
          approval_rate: 0,
          rejection_rate: 0,
          pending_rate: 0,
          average_claim_amount: 0,
          patient_contribution_rate: 0
        },
        monthly_trends: [],
        top_hospitals: [],
        rejection_analysis: [],
        metadata: {
          user_access_scope: '',
          report_generated_at: '',
          filters_applied: {}
        }
      },

      // Filters
      filters: {
        status: '',
        priority: '',
        hospitalId: null,
        startDate: '',
        endDate: ''
      },

      // Search
      searchQuery: '',
      searchTimeout: null,

      // Pagination
      perPage: 10,
      currentPage: 1,
      pagination: {
        total: 0,
        per_page: 10,
        current_page: 1,
        total_pages: 1,
        offset: 0,
        has_more: false
      },

      // UI States
      showDetailsModal: false,
      showProcessModal: false,
      showBatchModal: false,
      showSettlementModal: false,
      batchAction: '',

      // Alert states
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: ''
    };
  },

  computed: {
    // Ethiopian year options
    ethiopianYearOptions() {
      const years = [];
      for (let y = this.currentEthiopianDate.year - 20; y <= this.currentEthiopianDate.year + 5; y++) {
        years.push(y);
      }
      return years;
    },

    // Gregorian year options
    gregorianYearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let y = currentYear - 20; y <= currentYear + 5; y++) {
        years.push(y);
      }
      return years;
    },

    // Display months based on calendar type
    displayMonths() {
      return this.showEthiopianDates ? this.ethiopianMonths : this.gregorianMonths;
    },

    // Display years based on calendar type
    displayYearOptions() {
      return this.showEthiopianDates ? this.ethiopianYearOptions : this.gregorianYearOptions;
    },

    // Days in FROM month
    fromDaysInMonth() {
      if (this.showEthiopianDates) {
        return this.getDaysInEthiopianMonth(this.dateFilterRange.fromMonth, this.dateFilterRange.fromYear);
      } else {
        return this.getDaysInGregorianMonth(this.dateFilterRange.fromMonth, this.dateFilterRange.fromYear);
      }
    },

    // Days in TO month
    toDaysInMonth() {
      if (this.showEthiopianDates) {
        return this.getDaysInEthiopianMonth(this.dateFilterRange.toMonth, this.dateFilterRange.toYear);
      } else {
        return this.getDaysInGregorianMonth(this.dateFilterRange.toMonth, this.dateFilterRange.toYear);
      }
    },

    // Check if any date is selected
    hasDateSelection() {
      return (this.dateFilterRange.fromDay && this.dateFilterRange.fromMonth && this.dateFilterRange.fromYear) ||
          (this.dateFilterRange.toDay && this.dateFilterRange.toMonth && this.dateFilterRange.toYear);
    },

    // Check if date range is valid
    isValidDateRange() {
      return this.dateFilterRange.fromDay && this.dateFilterRange.fromMonth && this.dateFilterRange.fromYear &&
          this.dateFilterRange.toDay && this.dateFilterRange.toMonth && this.dateFilterRange.toYear;
    },

    // Display alternate calendar date
    alternateDateDisplay() {
      if (!this.isValidDateRange) return '';

      if (this.showEthiopianDates) {
        const fromGreg = this.ethiopianToGregorian(
            parseInt(this.dateFilterRange.fromYear),
            parseInt(this.dateFilterRange.fromMonth),
            parseInt(this.dateFilterRange.fromDay)
        );
        const toGreg = this.ethiopianToGregorian(
            parseInt(this.dateFilterRange.toYear),
            parseInt(this.dateFilterRange.toMonth),
            parseInt(this.dateFilterRange.toDay)
        );

        if (!fromGreg || !toGreg) return '';

        const formatDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        return `${formatDate(fromGreg)} — ${formatDate(toGreg)}`;
      } else {
        const fromEth = this.gregorianToEthiopian(new Date(
            parseInt(this.dateFilterRange.fromYear),
            parseInt(this.dateFilterRange.fromMonth) - 1,
            parseInt(this.dateFilterRange.fromDay)
        ));
        const toEth = this.gregorianToEthiopian(new Date(
            parseInt(this.dateFilterRange.toYear),
            parseInt(this.dateFilterRange.toMonth) - 1,
            parseInt(this.dateFilterRange.toDay)
        ));

        if (!fromEth || !toEth) return '';

        return `${fromEth.monthNameEn} ${fromEth.day}, ${fromEth.year} — ${toEth.monthNameEn} ${toEth.day}, ${toEth.year}`;
      }
    },

    hasActiveFilters() {
      return this.filters.status || this.filters.priority || this.filters.startDate || this.filters.endDate || this.searchQuery;
    },

    isAllSelected() {
      const selectableClaims = this.claims.filter(c => c.status === 'submitted');
      return selectableClaims.length > 0 && this.selectedClaims.length === selectableClaims.length;
    },

    showingFrom() {
      if (this.pagination.total === 0) return 0;
      return (this.pagination.current_page - 1) * this.pagination.per_page + 1;
    },

    showingTo() {
      const to = this.pagination.current_page * this.pagination.per_page;
      return Math.min(to, this.pagination.total);
    },

    visiblePages() {
      const totalPages = this.pagination.total_pages;
      const current = this.pagination.current_page;
      const pages = [];

      if (totalPages <= 5) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
      } else {
        let start = Math.max(1, current - 2);
        let end = Math.min(totalPages, current + 2);
        if (current <= 3) end = 5;
        if (current >= totalPages - 2) start = totalPages - 4;
        for (let i = start; i <= end; i++) pages.push(i);
      }
      return pages;
    }
  },

  watch: {
    showEthiopianDates() {
      this.dateFilterRange = { fromDay: '', fromMonth: '', fromYear: '', toDay: '', toMonth: '', toYear: '' };
    }
  },

  mounted() {
    this.currentEthiopianDate = this.getCurrentEthiopianDate();
    this.checkAuth();
    this.loadClaims();
    this.loadStatistics();
  },

  methods: {
    checkAuth() {
      const token = localStorage.getItem("authToken");
      if (!token) router.push('/login');
    },

    // ==================== ETHIOPIAN CALENDAR METHODS ====================

    getDaysInEthiopianMonth(month, year) {
      if (!month || !year) return 30;
      const m = parseInt(month);
      const y = parseInt(year);
      if (m === 13) return (y % 4 === 3) ? 6 : 5;
      return 30;
    },

    getDaysInGregorianMonth(month, year) {
      if (!month || !year) return 31;
      const m = parseInt(month);
      const y = parseInt(year);
      if ([1, 3, 5, 7, 8, 10, 12].includes(m)) return 31;
      if ([4, 6, 9, 11].includes(m)) return 30;
      if (m === 2) {
        const isLeap = (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);
        return isLeap ? 29 : 28;
      }
      return 31;
    },

    ethiopianToGregorian(ethYear, ethMonth, ethDay) {
      const ETHIOPIAN_EPOCH = 1724221;
      const jdn = ETHIOPIAN_EPOCH + 365 * (ethYear - 1) + Math.floor(ethYear / 4) + 30 * (ethMonth - 1) + ethDay - 1;

      const a = jdn + 32044;
      const b = Math.floor((4 * a + 3) / 146097);
      const c = a - Math.floor(146097 * b / 4);
      const d = Math.floor((4 * c + 3) / 1461);
      const e = c - Math.floor(1461 * d / 4);
      const m = Math.floor((5 * e + 2) / 153);

      const day = e - Math.floor((153 * m + 2) / 5) + 1;
      const month = m + 3 - 12 * Math.floor(m / 10);
      const year = 100 * b + d - 4800 + Math.floor(m / 10);

      return new Date(year, month - 1, day);
    },

    gregorianToEthiopian(gregDate) {
      const date = typeof gregDate === 'string' ? new Date(gregDate) : gregDate;
      if (!date || isNaN(date.getTime())) return null;

      const gYear = date.getFullYear();
      const gMonth = date.getMonth() + 1;
      const gDay = date.getDate();

      const a = Math.floor((14 - gMonth) / 12);
      const y = gYear + 4800 - a;
      const m = gMonth + 12 * a - 3;
      const jdn = gDay + Math.floor((153 * m + 2) / 5) + 365 * y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400) - 32045;

      const ETHIOPIAN_EPOCH = 1724221;
      const r = (jdn - ETHIOPIAN_EPOCH) % 1461;
      const n = r % 365 + 365 * Math.floor(r / 1460);

      const ethYear = 4 * Math.floor((jdn - ETHIOPIAN_EPOCH) / 1461) + Math.floor(r / 365) - Math.floor(r / 1460) + 1;
      const ethMonth = Math.floor(n / 30) + 1;
      const ethDay = n % 30 + 1;

      const monthData = this.ethiopianMonths.find(mon => mon.value === ethMonth);

      return { year: ethYear, month: ethMonth, day: ethDay, monthName: monthData?.label || '', monthNameEn: monthData?.labelEn || '' };
    },

    getCurrentEthiopianDate() {
      return this.gregorianToEthiopian(new Date());
    },

    formatDateDisplay(dateString) {
      if (!dateString) return '-';

      if (this.showEthiopianDates) {
        const eth = this.gregorianToEthiopian(dateString);
        if (eth) return `${eth.monthNameEn} ${eth.day}, ${eth.year}`;
      }

      const d = new Date(dateString);
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    },

    formatDateTimeDisplay(dateTimeString) {
      if (!dateTimeString) return 'Not available';

      if (this.showEthiopianDates) {
        const eth = this.gregorianToEthiopian(dateTimeString);
        if (eth) {
          const d = new Date(dateTimeString);
          return `${eth.monthNameEn} ${eth.day}, ${eth.year} ${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
        }
      }

      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },

    formatMonthYearDisplay(monthYear) {
      if (!monthYear) return '';
      const [year, month] = monthYear.split('-');

      if (this.showEthiopianDates) {
        const gregDate = new Date(parseInt(year), parseInt(month) - 1, 15);
        const eth = this.gregorianToEthiopian(gregDate);
        if (eth) return `${eth.monthNameEn} ${eth.year}`;
      }

      const date = new Date(year, month - 1);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    },

    onFromMonthChange() {
      if (this.dateFilterRange.fromDay > this.fromDaysInMonth) {
        this.dateFilterRange.fromDay = this.fromDaysInMonth;
      }
      this.updateDateRange();
    },

    onToMonthChange() {
      if (this.dateFilterRange.toDay > this.toDaysInMonth) {
        this.dateFilterRange.toDay = this.toDaysInMonth;
      }
      this.updateDateRange();
    },

    updateDateRange() {
      if (this.dateFilterRange.fromDay && this.dateFilterRange.fromMonth && this.dateFilterRange.fromYear) {
        let fromGreg;
        if (this.showEthiopianDates) {
          fromGreg = this.ethiopianToGregorian(parseInt(this.dateFilterRange.fromYear), parseInt(this.dateFilterRange.fromMonth), parseInt(this.dateFilterRange.fromDay));
        } else {
          fromGreg = new Date(parseInt(this.dateFilterRange.fromYear), parseInt(this.dateFilterRange.fromMonth) - 1, parseInt(this.dateFilterRange.fromDay));
        }
        if (fromGreg) this.filters.startDate = fromGreg.toISOString().split('T')[0];
      }

      if (this.dateFilterRange.toDay && this.dateFilterRange.toMonth && this.dateFilterRange.toYear) {
        let toGreg;
        if (this.showEthiopianDates) {
          toGreg = this.ethiopianToGregorian(parseInt(this.dateFilterRange.toYear), parseInt(this.dateFilterRange.toMonth), parseInt(this.dateFilterRange.toDay));
        } else {
          toGreg = new Date(parseInt(this.dateFilterRange.toYear), parseInt(this.dateFilterRange.toMonth) - 1, parseInt(this.dateFilterRange.toDay));
        }
        if (toGreg) this.filters.endDate = toGreg.toISOString().split('T')[0];
      }
    },

    applyDateFilter() {
      if (this.isValidDateRange) {
        this.updateDateRange();
        this.resetPagination();
        this.loadClaims();
        this.loadStatistics();
      }
    },

    clearDateFilter() {
      this.dateFilterRange = { fromDay: '', fromMonth: '', fromYear: '', toDay: '', toMonth: '', toYear: '' };
      this.filters.startDate = '';
      this.filters.endDate = '';
      this.resetPagination();
      this.loadClaims();
      this.loadStatistics();
    },

    setQuickDateRange(period) {
      const today = new Date();
      let startDate = new Date();
      let endDate = new Date();

      switch(period) {
        case 'today': startDate = new Date(); break;
        case 'week': startDate.setDate(today.getDate() - 7); break;
        case 'month': startDate.setDate(1); break;
      }

      this.filters.startDate = startDate.toISOString().split('T')[0];
      this.filters.endDate = endDate.toISOString().split('T')[0];

      if (this.showEthiopianDates) {
        const startEth = this.gregorianToEthiopian(startDate);
        const endEth = this.gregorianToEthiopian(endDate);
        if (startEth && endEth) {
          this.dateFilterRange.fromDay = startEth.day;
          this.dateFilterRange.fromMonth = startEth.month;
          this.dateFilterRange.fromYear = startEth.year;
          this.dateFilterRange.toDay = endEth.day;
          this.dateFilterRange.toMonth = endEth.month;
          this.dateFilterRange.toYear = endEth.year;
        }
      } else {
        this.dateFilterRange.fromDay = startDate.getDate();
        this.dateFilterRange.fromMonth = startDate.getMonth() + 1;
        this.dateFilterRange.fromYear = startDate.getFullYear();
        this.dateFilterRange.toDay = endDate.getDate();
        this.dateFilterRange.toMonth = endDate.getMonth() + 1;
        this.dateFilterRange.toYear = endDate.getFullYear();
      }

      this.resetPagination();
      this.loadClaims();
      this.loadStatistics();
    },

    // ==================== PAGINATION METHODS ====================

    getRowNumber(index) {
      return (this.pagination.current_page - 1) * this.pagination.per_page + index + 1;
    },

    resetPagination() {
      this.currentPage = 1;
      this.pagination = { total: 0, per_page: this.perPage, current_page: 1, total_pages: 1, offset: 0, has_more: false };
    },

    previousPage() {
      if (this.pagination.current_page > 1) this.goToPage(this.pagination.current_page - 1);
    },

    nextPage() {
      if (this.pagination.current_page < this.pagination.total_pages) this.goToPage(this.pagination.current_page + 1);
    },

    goToPage(page) {
      if (page < 1 || page > this.pagination.total_pages) return;
      this.currentPage = page;
      this.loadClaims();
    },

    onPerPageChange() {
      this.resetPagination();
      this.loadClaims();
    },

    refreshData() {
      this.searchQuery = '';
      this.resetPagination();
      this.loadClaims();
      this.loadStatistics();
    },

    handleSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.resetPagination();
        this.loadClaims();
      }, 300);
    },

    // ==================== API CALLS ====================

    async loadClaims() {
      this.loading = true;
      const offset = (this.currentPage - 1) * this.perPage;

      const data = {
        "function": "getClaimsForProcessing",
        "status": this.filters.status,
        "priority": this.filters.priority,
        "hospitalId": this.filters.hospitalId,
        "startDate": this.filters.startDate,
        "endDate": this.filters.endDate,
        "searchTerm": this.searchQuery,
        "limit": this.perPage,
        "offset": offset
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.claims = res.data.data.claims || [];
          this.pagination = res.data.data.pagination || this.pagination;
        } else {
          this.showAlert('failed', res.data.message || 'Failed to load claims');
        }
      } catch (e) {
        console.error("Failed to load claims:", e);
        this.showAlert('failed', 'Failed to load claims');
      } finally {
        this.loading = false;
      }
    },

    async loadStatistics() {
      const data = { "function": "getClaimsStatistics", "startDate": this.filters.startDate, "endDate": this.filters.endDate };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success && res.data.data) {
          this.statistics = {
            summary_statistics: res.data.data.summary_statistics || {},
            monthly_trends: res.data.data.monthly_trends || [],
            top_hospitals: res.data.data.top_hospitals || [],
            rejection_analysis: res.data.data.rejection_analysis || [],
            metadata: res.data.data.metadata || {}
          };
        }
      } catch (e) {
        console.error("Failed to load statistics:", e);
      }
    },

    // ==================== FILTER METHODS ====================

    filterByStatus(status) {
      this.filters.status = status;
      this.resetPagination();
      this.loadClaims();
    },

    filterByPriority(priority) {
      this.filters.priority = priority;
      this.resetPagination();
      this.loadClaims();
    },

    async clearFilters() {
      this.filters = { status: '', priority: '', hospitalId: null, startDate: '', endDate: '' };
      this.dateFilterRange = { fromDay: '', fromMonth: '', fromYear: '', toDay: '', toMonth: '', toYear: '' };
      this.searchQuery = '';
      this.resetPagination();
      await this.loadClaims();
      await this.loadStatistics();
    },

    // ==================== FORMATTING METHODS ====================

    formatDecimal(value, decimals = 1) {
      if (!value) return '0';
      return parseFloat(value).toFixed(decimals);
    },

    formatAccessScope(scope) {
      const scopes = { 'national': 'National Level', 'regional': 'Regional Level', 'zonal': 'Zonal Level', 'woreda': 'Woreda Level', 'hospital': 'Hospital Level' };
      return scopes[scope] || scope;
    },

    formatRejectionCategory(category) {
      const categories = { 'missing_documents': 'Missing Documents', 'invalid_member': 'Invalid Member', 'coverage_exceeded': 'Coverage Exceeded', 'service_not_covered': 'Service Not Covered', 'duplicate_claim': 'Duplicate Claim', 'fraud_suspected': 'Fraud Suspected', 'other': 'Other Reasons' };
      return categories[category] || category.replace('_', ' ').toUpperCase();
    },

    truncateText(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },

    getProcessingTimeClass(days) {
      if (!days) return 'text-muted';
      if (parseFloat(days) <= 3) return 'text-success';
      if (parseFloat(days) <= 7) return 'text-warning';
      return 'text-danger';
    },

    getProcessingTimeIcon(days) {
      if (!days) return 'bx bx-minus';
      if (parseFloat(days) <= 3) return 'bx bx-trending-down';
      if (parseFloat(days) <= 7) return 'bx bx-minus';
      return 'bx bx-trending-up';
    },

    getProcessingTimeLabel(days) {
      if (!days) return 'No data';
      const numDays = parseFloat(days);
      if (numDays <= 3) return 'Excellent';
      if (numDays <= 7) return 'Good';
      return 'Needs improvement';
    },

    getApprovalReasonLabel(reason) {
      if (!reason) return '';
      const parts = reason.split(':');
      return parts.length > 1 ? parts[1].trim() : reason;
    },

    getRejectionReasonLabel(reason) {
      if (!reason) return '';
      const parts = reason.split(':');
      return parts.length > 1 ? parts[1].trim() : reason;
    },

    getUtilizationPercentage(claim) {
      if (claim.coverage_type === 'unlimited' || !claim.annual_limit || claim.annual_limit === 0) return 0;
      const consumed = claim.year_consumed_amount || 0;
      return Math.min(100, (consumed / claim.annual_limit) * 100);
    },

    getUtilizationClass(claim) {
      if (claim.coverage_type === 'unlimited' || !claim.annual_limit || claim.annual_limit === 0) return 'bg-success';
      const percentage = this.getUtilizationPercentage(claim);
      if (percentage >= 90) return 'bg-danger';
      if (percentage >= 70) return 'bg-warning';
      return 'bg-success';
    },

    // ==================== CLAIM ACTIONS ====================

    async viewClaimDetails(claim) {
      this.selectedClaim = claim;
      this.showDetailsModal = true;
    },

    processClaim(claim) {
      this.selectedClaim = claim;
      if (this.showDetailsModal) this.closeDetailsModal();
      this.showProcessModal = true;
    },

    async approveClaim(claimId, approvedAmount, approvalReason, processingNotes) {
      const data = { "function": "processClaim", "claimId": claimId, "action": "approve", "approvedAmount": approvedAmount, "approvalReason": approvalReason, "processingNotes": processingNotes };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          let successMessage = 'Claim approved successfully';
          if (res.data.data?.approvedAmount) successMessage += ` for ETB ${this.formatAmount(res.data.data.approvedAmount)}`;
          if (res.data.data?.patientContribution > 0) successMessage += ` (Patient contribution: ETB ${this.formatAmount(res.data.data.patientContribution)})`;
          this.showAlert('success', successMessage);
          this.closeProcessModal();
          await this.loadClaims();
          await this.loadStatistics();
        } else {
          this.showAlert('failed', res.data.message || 'Failed to approve claim');
        }
      } catch (e) {
        console.error("Failed to approve claim:", e);
        this.showAlert('failed', 'Failed to approve claim');
      }
    },

    async rejectClaim(claimId, rejectionReason, processingNotes) {
      const data = { "function": "processClaim", "claimId": claimId, "action": "reject", "rejectionReason": rejectionReason, "processingNotes": processingNotes };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.showAlert('success', 'Claim rejected successfully');
          this.closeProcessModal();
          await this.loadClaims();
          await this.loadStatistics();
        } else {
          this.showAlert('failed', res.data.message || 'Failed to reject claim');
        }
      } catch (e) {
        console.error("Failed to reject claim:", e);
        this.showAlert('failed', 'Failed to reject claim');
      }
    },

    // ==================== BATCH OPERATIONS ====================

    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedClaims = [];
      } else {
        this.selectedClaims = this.claims.filter(c => c.status === 'submitted').map(c => c.claim_id);
      }
    },

    clearSelection() {
      this.selectedClaims = [];
    },

    batchApprove() {
      if (this.selectedClaims.length === 0) return;
      this.batchAction = 'approve';
      this.selectedClaimsData = this.claims.filter(c => this.selectedClaims.includes(c.claim_id));
      this.showBatchModal = true;
    },

    batchReject() {
      if (this.selectedClaims.length === 0) return;
      this.batchAction = 'reject';
      this.selectedClaimsData = this.claims.filter(c => this.selectedClaims.includes(c.claim_id));
      this.showBatchModal = true;
    },

    async processBatch(action, reasons = {}) {
      const data = { "function": "processClaimsBatch", "claimIds": this.selectedClaims, "action": action, "reasons": reasons };
      if (typeof reasons === 'string') data.rejectionReason = reasons;

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          let successMessage = res.data.message || `Successfully ${action}ed claims`;
          if (res.data.data?.totalApprovedAmount && action === 'approve') successMessage += ` for ETB ${this.formatAmount(res.data.data.totalApprovedAmount)}`;
          if (res.data.data?.skipped > 0) successMessage += `. ${res.data.data.skipped} claims were skipped`;
          this.showAlert('success', successMessage);
          this.closeBatchModal();
          this.clearSelection();
          await this.loadClaims();
          await this.loadStatistics();
        } else {
          this.showAlert('failed', res.data.message || 'Failed to process claims');
        }
      } catch (e) {
        console.error("Failed to process batch:", e);
        this.showAlert('failed', 'Failed to process batch');
      }
    },

    // ==================== MODAL CONTROLS ====================

    closeDetailsModal() {
      this.showDetailsModal = false;
    },

    closeProcessModal() {
      this.showProcessModal = false;
      this.selectedClaim = null;
    },

    closeBatchModal() {
      this.showBatchModal = false;
      this.batchAction = '';
      this.selectedClaimsData = [];
    },

    async onSettlementCreated() {
      this.showSettlementModal = false;
      this.showAlert('success', 'Settlement batch created successfully');
      await this.loadClaims();
      await this.loadStatistics();
    },

    // ==================== UTILITY METHODS ====================

    formatAmount(amount) {
      return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    },

    getInitials(fullName) {
      if (!fullName) return '';
      const parts = fullName.trim().split(' ');
      if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },

    getStatusLabel(status) {
      const labels = { 'submitted': 'Submitted', 'approved': 'Approved', 'rejected': 'Rejected', 'paid': 'Paid' };
      return labels[status] || status;
    },

    getPriorityLabel(priority) {
      const labels = { 'urgent': 'Urgent (>7 days)', 'high': 'High (4-7 days)', 'normal': 'Normal (<4 days)' };
      return labels[priority] || priority;
    },

    getStatusBadge(status) {
      const badges = { 'submitted': 'badge bg-warning', 'approved': 'badge bg-success', 'rejected': 'badge bg-danger', 'paid': 'badge bg-info' };
      return badges[status] || 'badge bg-secondary';
    },

    getPriorityBadge(days) {
      if (days > 7) return 'badge bg-danger';
      if (days >= 4) return 'badge bg-warning';
      return 'badge bg-primary';
    },

    // ==================== ALERT METHODS ====================

    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;
      if (type === 'success' || type === 'info') setTimeout(() => this.clearAlerts(), 5000);
    }
  }
};
</script>

<style scoped>
.spinner-border { width: 2rem; height: 2rem; }
.search-icon { position: absolute; top: 50%; right: 10px; transform: translateY(-50%); color: #999; }
.avatar-title { display: flex; align-items: center; justify-content: center; width: 100%; height: 100%; font-weight: 600; }
.table-danger { background-color: rgba(220, 53, 69, 0.05); }
.progress { background-color: #e9ecef; }
.form-check-input { cursor: pointer; }
.filter-tool-bar-btns .btn-group { margin-right: 5px; }
.mini-stats-wid { border-left: 3px solid transparent; transition: all 0.3s ease; border-radius: 8px; }
.mini-stats-wid:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1); }
.card { border-radius: 8px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05); }
.card-header { border-bottom: 1px solid #f1f1f1; border-radius: 8px 8px 0 0; }
.progress { border-radius: 10px; }
.progress-bar { border-radius: 10px; }
.bg-danger-subtle { background-color: rgba(220, 53, 69, 0.1); }
.bg-success-subtle { background-color: rgba(40, 167, 69, 0.1); }
.table-hover tbody tr:hover { background-color: rgba(0, 0, 0, 0.075); }
.gap-3 { gap: 1rem; }
.pagination { margin-bottom: 0; }
.page-link { padding: 0.25rem 0.5rem; font-size: 0.875rem; }
.page-item.active .page-link { background-color: #0d6efd; border-color: #0d6efd; }
.page-item.disabled .page-link { color: #6c757d; pointer-events: none; background-color: #fff; border-color: #dee2e6; }
.bx-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
.mini-stats-wid { animation: fadeInUp 0.3s ease-out; }
.btn-check:checked + .btn-outline-primary { background-color: #0d6efd; color: white; }
.btn-check:checked + .btn-outline-success { background-color: #198754; color: white; }
@media (max-width: 768px) { .filter-tool-bar-btns { flex-wrap: wrap; } .btn-group { margin-bottom: 5px; } .table-responsive { font-size: 0.875rem; } .mini-stats-wid { margin-bottom: 1rem; } .card { margin-bottom: 1rem; } }
@media (max-width: 576px) { .col-lg-3, .col-lg-6, .col-lg-4 { margin-bottom: 1rem; } .avatar-sm { width: 35px; height: 35px; } .font-size-20 { font-size: 16px !important; } }
.alert { min-width: 300px; box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15); border-radius: 8px; }
.badge { font-size: 0.75rem; padding: 0.35em 0.65em; }
.table-responsive::-webkit-scrollbar { height: 8px; }
.table-responsive::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
.table-responsive::-webkit-scrollbar-thumb { background: #888; border-radius: 10px; }
.table-responsive::-webkit-scrollbar-thumb:hover { background: #555; }
.dropdown-menu { border-radius: 8px; box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1); border: none; }
.dropdown-item:hover { background-color: #f8f9fa; }
</style>